import { zodResolver } from "@hookform/resolvers/zod";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  type Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect } from "react";
import {
  type FieldErrors,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useSaveActorPriceCategoriesMutation } from "Actors/Api/Mutations/useSaveActorPriceCategoriesMutation";
import { useActorPriceCategoriesQuery } from "Actors/Api/Queries/useActorPriceCategoriesQuery";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import {
  getActorPriceCategoryApprovalTypeOptions,
  getActorPriceCategoryStatusOptions,
} from "Forms/Utils/EnumOptions";
import {
  ActorPriceCategoryApprovalType,
  ActorPriceCategoryStatus,
} from "Infrastructure/Api/Api";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { usePriceCategoriesListQuery } from "PriceCategories/Api/Queries/usePriceCategoriesListQuery";
import { BlModal } from "Shared/Dialogs/BlModal";
import { Price } from "Shared/Price/Price";

const required = getTranslation(Resources.Validation.Required);

const rowSchema = z
  .object({
    priceCategoryID: z.string(),
    priceCategoryName: z.string(),
    commissionRate: z.number(),
    priceCategoryType: z.string(),
    defaultPrice: z.number().nullable(),
    price: z.number().nullable(),
    note: z.string().nullable(),
    status: z.union([z.enum(ActorPriceCategoryStatus), z.literal("")]),
    approvalType: z.union([
      z.enum(ActorPriceCategoryApprovalType),
      z.literal(""),
    ]),
  })
  .superRefine((row, ctx) => {
    if (row.status !== ActorPriceCategoryStatus.Allowed) return;
    if (row.price === null) {
      ctx.addIssue({ code: "custom", message: required, path: ["price"] });
    }
    if (row.approvalType === "") {
      ctx.addIssue({
        code: "custom",
        message: required,
        path: ["approvalType"],
      });
    }
  });

const formSchema = z.object({
  rows: z.array(rowSchema),
});

type FormData = z.infer<typeof formSchema>;
type RowData = z.infer<typeof rowSchema>;

function computeFinalPrice(
  price: number | null,
  commissionRate: number,
): number | null {
  if (price === null || price === undefined) return null;
  return price + price * commissionRate;
}

function PriceSuffix({ type }: { type: string }): React.ReactNode {
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{ color: "text.secondary", ml: 0.5 }}
    >
      {type === "PerMinute"
        ? getTranslation(Resources.PriceCategories.PriceSuffix.PerMinute)
        : getTranslation(Resources.PriceCategories.PriceSuffix.PerJob)}
    </Typography>
  );
}

interface RowFinalPriceProps {
  index: number;
  control: ReturnType<typeof useForm<FormData>>["control"];
  commissionRate: number;
  type: string;
}

function RowFinalPrice({
  index,
  control,
  commissionRate,
  type,
}: RowFinalPriceProps): React.ReactNode {
  const price = useWatch({ control, name: `rows.${index}.price` });
  const finalPrice = computeFinalPrice(price, commissionRate);
  if (finalPrice === null)
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        -
      </Typography>
    );
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        <Price amount={finalPrice} />
      </Typography>
      <PriceSuffix type={type} />
    </Box>
  );
}

interface EditActorPriceCategoriesModalProps {
  actorID: string;
  isOpen: boolean;
  onClose: () => void;
}

export const EditActorPriceCategoriesModal = ({
  actorID,
  isOpen,
  onClose,
}: EditActorPriceCategoriesModalProps): React.ReactNode => {
  const { t } = useResource();
  const saveMutation = useSaveActorPriceCategoriesMutation(actorID);

  const { data: allCategories, isLoading: isLoadingAll } =
    usePriceCategoriesListQuery({ limit: 200 });
  const { data: actorCategories, isLoading: isLoadingActor } =
    useActorPriceCategoriesQuery(actorID);

  const isLoading = isLoadingAll || isLoadingActor;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { rows: [] },
  });

  const { fields } = useFieldArray({ control, name: "rows" });

  useEffect(() => {
    if (!isOpen || !allCategories || !actorCategories) return;

    const actorMap = new Map(
      actorCategories.map((c) => [c.priceCategoryID, c]),
    );

    const rows: RowData[] = allCategories.items.map((cat) => {
      const actorData = actorMap.get(cat.priceCategoryID);
      const customPrice = actorData?.price ?? null;
      return {
        priceCategoryID: cat.priceCategoryID,
        priceCategoryName: cat.name,
        commissionRate: cat.commissionRate,
        priceCategoryType: cat.type,
        defaultPrice: cat.defaultPrice ?? null,
        price: customPrice ?? cat.defaultPrice ?? null,
        note: actorData?.note ?? null,
        status: actorData?.status ?? "",
        approvalType:
          actorData?.approvalType ?? ActorPriceCategoryApprovalType.Automatic,
      };
    });

    reset({ rows });
  }, [isOpen, allCategories, actorCategories, reset]);

  const onSubmit = async (data: FormData) => {
    const items = data.rows
      .filter((row) => row.status !== "")
      .map((row) => ({
        priceCategoryID: row.priceCategoryID,
        price: row.price ?? 0,
        note: row.note || undefined,
        status: row.status as ActorPriceCategoryStatus,
        approvalType:
          row.approvalType !== ""
            ? (row.approvalType as ActorPriceCategoryApprovalType)
            : undefined,
      }));

    try {
      await saveMutation.mutateAsync({ items });
      toast.success(
        t(
          Resources.Actors.ActorDetail.PriceCategories.SaveAmendment
            .SuccessMessage,
        ),
      );
      onClose();
    } catch {
      // error handled by mutation (auto-toast via processResponse)
    }
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Actors.ActorDetail.NewAmendment)}
      onCancel={onClose}
      onConfirm={handleSubmit(onSubmit)}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Common.Save)}
      maxWidth="1300px"
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: Spacing.M }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            sx={{ minWidth: 900, "& td, & th": { borderBottom: "none" } }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    minWidth: 200,
                    color: "text.secondary",
                    pb: 1.5,
                  }}
                >
                  {t(Resources.Actors.ActorDetail.PriceCategories.PricingModel)}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 160, color: "text.secondary", pb: 1.5 }}
                >
                  {t(Resources.Actors.ActorDetail.PriceCategories.ActorsCut)}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 90, color: "text.secondary", pb: 1.5 }}
                >
                  {t(Resources.Actors.ActorDetail.PriceCategories.Commission)}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 140, color: "text.secondary", pb: 1.5 }}
                >
                  {t(Resources.Actors.ActorDetail.PriceCategories.FinalPrice)}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 200, color: "text.secondary", pb: 1.5 }}
                >
                  {t(Resources.Actors.ActorDetail.PriceCategories.Notes)}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 140, color: "text.secondary", pb: 1.5 }}
                >
                  {t(Resources.Actors.ActorDetail.PriceCategories.State)}
                </TableCell>
                <TableCell
                  sx={{ minWidth: 140, color: "text.secondary", pb: 1.5 }}
                >
                  {t(Resources.Actors.ActorDetail.PriceCategories.Approval)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => (
                <RowCells
                  key={field.id}
                  index={index}
                  control={control}
                  errors={errors}
                  t={t}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </BlModal>
  );
};

interface RowCellsProps {
  index: number;
  control: ReturnType<typeof useForm<FormData>>["control"];
  errors: FieldErrors<FormData>;
  t: (key: string) => string;
}

function getStatusColor(status: string, palette: Theme["palette"]): string {
  switch (status) {
    case ActorPriceCategoryStatus.Allowed:
      return palette.success.main;
    case ActorPriceCategoryStatus.Exclusive:
      return palette.warning.main;
    default:
      return palette.error.main;
  }
}

function RowCells({
  index,
  control,
  errors,
  t,
}: RowCellsProps): React.ReactNode {
  const { palette } = useTheme();

  const status = useWatch({ control, name: `rows.${index}.status` });
  const commissionRate = useWatch({
    control,
    name: `rows.${index}.commissionRate`,
  });
  const priceCategoryType = useWatch({
    control,
    name: `rows.${index}.priceCategoryType`,
  });
  const defaultPrice = useWatch({
    control,
    name: `rows.${index}.defaultPrice`,
  });
  const priceCategoryName = useWatch({
    control,
    name: `rows.${index}.priceCategoryName`,
  });

  const isApprovalDisabled = status === "";

  const priceSuffix =
    priceCategoryType === "PerMinute"
      ? getTranslation(Resources.PriceCategories.PriceSuffix.PerMinute)
      : getTranslation(Resources.PriceCategories.PriceSuffix.PerJob);

  const defaultPriceFormatted =
    defaultPrice !== null && defaultPrice !== undefined
      ? `${formatCurrency(defaultPrice, CurrencyCode.EUR)} ${priceSuffix}`
      : "-";

  const statusColor = getStatusColor(status, palette);

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {priceCategoryName}
        </Typography>
      </TableCell>

      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <BlFormInput
            name={`rows.${index}.price`}
            control={control}
            errors={errors}
            size="small"
            disabled={status === ""}
            sx={{ width: 100 }}
            inputProps={{ min: 0, step: 0.01 }}
            isNumber
          />
          <PriceSuffix type={priceCategoryType} />
          <Tooltip
            title={`${t(Resources.Actors.ActorDetail.PriceCategories.DefaultPriceTooltip)}: ${defaultPriceFormatted}`}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 14,
                color: "text.secondary",
                cursor: "help",
                flexShrink: 0,
              }}
            />
          </Tooltip>
        </Box>
      </TableCell>

      <TableCell>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`${(commissionRate * 100).toFixed(0)} %`}
        </Typography>
      </TableCell>

      <TableCell>
        <RowFinalPrice
          index={index}
          control={control}
          commissionRate={commissionRate}
          type={priceCategoryType}
        />
      </TableCell>

      <TableCell>
        <BlFormInput
          name={`rows.${index}.note`}
          control={control}
          errors={errors}
          size="small"
          disabled={status === ""}
          sx={{ width: "100%" }}
        />
      </TableCell>

      <TableCell>
        <BlFormDropdown
          name={`rows.${index}.status`}
          control={control}
          errors={errors}
          options={getActorPriceCategoryStatusOptions()}
          size="small"
          SelectDisplayProps={{ style: { color: statusColor } }}
          showEmptyOptionLabel
        />
      </TableCell>

      <TableCell>
        {isApprovalDisabled ? (
          <Typography variant="body2" sx={{ color: "text.secondary", px: 1 }}>
            -
          </Typography>
        ) : (
          <BlFormDropdown
            name={`rows.${index}.approvalType`}
            control={control}
            errors={errors}
            options={getActorPriceCategoryApprovalTypeOptions()}
            size="small"
          />
        )}
      </TableCell>
    </TableRow>
  );
}
